import { useState, useCallback, useRef, useEffect } from "react";
import { ChatAPI, ChatRequest } from "../api/chat";
import { Message } from "../types";
import { useNotification } from "../contexts/NotificationContext";
import { useReport } from "../contexts/ReportContext";

interface UseChatResult {
  messages: Message[];
  hasChatStarted: boolean;
  isProcessing: boolean;
  isTyping: boolean;
  statusMessages: string[];
  sendMessage: (
    projectId: string,
    userInput: string,
    threadId?: string,
    workspaceId?: string
  ) => Promise<void>;
  resetSession: (projectId: string) => Promise<void>;
  clearMessages: () => void;
}

export function useChat(): UseChatResult {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [statusMessages, setStatusMessages] = useState<string[]>([]);
  const { addNotification, clearAll: clearNotifications } = useNotification();
  const {
    updateOverview,
    updateSpecificationAnalysis,
    updateReport,
    updateThreadId,
    updateAnalysisHistory,
    clearAll,
    isResetting,
    setResetting,
    isAutoTriggeringFromUrl,
    setIsAutoTriggeringFromUrl,
    setHasChatStarted,
  } = useReport();
  const threadIdRef = useRef<string>("");

  // Clear auto-trigger flag when messages have been updated (no arbitrary timeout)
  useEffect(() => {
    if (messages.length > 0 && isAutoTriggeringFromUrl) {
      setIsAutoTriggeringFromUrl(false);
    }
  }, [messages.length, isAutoTriggeringFromUrl, setIsAutoTriggeringFromUrl]);
  const currentStepRef = useRef<string>("");
  // 送信開始後、最初のチャンクかどうかを判定
  const firstChunkRef = useRef<boolean>(false);
  // 直前セッションで確定したAI応答（送信直前にスナップショット）
  const previousAiContentRef = useRef<string>("");
  // 今回の応答で生成したAIメッセージID（最初の有効チャンクで作成）
  const aiMessageIdRef = useRef<string>("");

  // ストリーミング中に受信した最後のAI応答を保存
  const lastAiResponseRef = useRef<string>("");
  // ストリーミング完了フラグ
  const streamCompletedRef = useRef<boolean>(false);

  // Stream cancellation - create a reference to abort from anywhere
  const abortControllerRef = useRef<AbortController | null>(null);
  // Current session ID
  const currentSessionIdRef = useRef<string>("");


  const sendMessage = useCallback(
    async (projectId: string, userInput: string, threadId?: string, workspaceId?: string) => {
      if (isProcessing || !userInput.trim()) return;

      // Mark chat as started so right panel can show data (only this instance sends messages)
      setHasChatStarted(true);

      // Generate a new session ID for this request
      const sessionId = Date.now().toString();
      currentSessionIdRef.current = sessionId;

      // Cancel any existing stream
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new abort controller for this request
      abortControllerRef.current = new AbortController();

      const userMessage: Message = {
        id: Date.now().toString(),
        content: userInput.trim(),
        type: "user",
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, userMessage]);
      setIsProcessing(true);
      setIsTyping(true);
      setStatusMessages([]);
      currentStepRef.current = "";
      // 次回送信用にフラグを初期化
      firstChunkRef.current = true;
      // 直前の確定AI応答をスナップショット
      const lastAi = [...messages].reverse().find((m) => m.type === "ai");
      previousAiContentRef.current = lastAi?.content || "";
      aiMessageIdRef.current = "";
      lastAiResponseRef.current = "";
      streamCompletedRef.current = false;

      try {
        const request: ChatRequest = {
          project_id: projectId,
          workspace_id: workspaceId || "",
          user_input: userInput.trim(),
          thread_id: threadId || threadIdRef.current,
        };

        let aiResponseContent = "";
        // 一時AIメッセージは追加しない（最初の有効チャンクで作成）

        // Stream the response with AbortSignal
        for await (const chunk of ChatAPI.streamChat(request, abortControllerRef.current.signal)) {
          // Check if this is still the current session
          if (currentSessionIdRef.current !== sessionId) {
            console.log("Session changed, stopping processing");
            break;
          }

          // Check if request was aborted
          if (abortControllerRef.current?.signal.aborted) {
            console.log("Stream aborted, stopping processing");
            break;
          }

          // Don't process chunks if we're resetting
          if (isResetting) {
            console.log("Skipping chunk processing due to reset in progress");
            break;
          }

          console.log("Received chunk in useChat:", chunk);

          if (chunk.error) {
            throw new Error(chunk.error);
          }

          // Handle status messages
          if (chunk.next_step && chunk.next_step !== currentStepRef.current) {
            console.log("Processing next_step:", chunk.next_step);
            currentStepRef.current = chunk.next_step;
            const newMessage = getStatusMessage(chunk.next_step);
            console.log("Generated status message:", newMessage);
            if (newMessage) {
              setStatusMessages((prev) => {
                console.log("Current status messages:", prev);
                const updated = [...prev, newMessage];
                console.log("Updated status messages:", updated);
                return updated;
              });
            }
          }

          // Handle AI response for chat area
          if (chunk.ai_response) {
            aiResponseContent = chunk.ai_response;
            lastAiResponseRef.current = aiResponseContent;

            // 初回チャンクが直前のAI応答と同一ならスキップ（表示しない）
            if (
              firstChunkRef.current &&
              aiResponseContent.trim() === previousAiContentRef.current.trim()
            ) {
              firstChunkRef.current = false;
              continue;
            }

            firstChunkRef.current = false;

            // まだAIメッセージを作成していなければ作成し、以後は内容更新
            if (!aiMessageIdRef.current) {
              aiMessageIdRef.current = (Date.now() + 1).toString();
              const newAiMessage: Message = {
                id: aiMessageIdRef.current,
                content: aiResponseContent,
                type: "ai",
                timestamp: new Date(),
                isProcessing: true,
              };
              setMessages((prev) => [...prev, newAiMessage]);
            } else {
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.id === aiMessageIdRef.current
                    ? { ...msg, content: aiResponseContent }
                    : msg
                )
              );
            }
          }

          // Handle other data for right panel
          // Prefer `overview` (static version field) but also support legacy `analyzed_info`
          if (chunk.overview) {
            console.log("Updating overview with overview:", chunk.overview);
            updateOverview(chunk.overview);
          } else if (chunk.analyzed_info) {
            console.log(
              "Updating overview with analyzed_info:",
              chunk.analyzed_info
            );
            updateOverview(chunk.analyzed_info);
          }

          if (chunk.report) {
            console.log("Updating report:", chunk.report);
            updateReport(chunk.report);
          }

          if (chunk.thread_id) {
            threadIdRef.current = chunk.thread_id;
            updateThreadId(chunk.thread_id);
          }

          // Handle analysis history from interaction_history
          if (chunk.interaction_history) {
            console.log(
              "Processing interaction_history:",
              chunk.interaction_history
            );

            const analysisHistory = chunk.interaction_history
              .filter((item: any) => item.type === "analysis_update")
              .map((item: any) => ({
                stage: item.question,
                content: item.response,
                timestamp: item.timestamp,
              }));

            console.log("Filtered analysis history:", analysisHistory);
            if (analysisHistory.length > 0) {
              updateAnalysisHistory(analysisHistory);
            }

            // Handle specification analysis from interaction_history
            const specificationAnalysis = chunk.interaction_history
              .filter((item: any) => item.type === "specification")
              .map((item: any) => ({
                question: item.question,
                result: item.response,
                timestamp: item.timestamp,
              }));

            console.log(
              "Filtered specification analysis:",
              specificationAnalysis
            );
            if (specificationAnalysis.length > 0) {
              updateSpecificationAnalysis(specificationAnalysis);
            }
          }
        }

        // ストリーミング完了をマーク
        streamCompletedRef.current = true;
        console.log("Stream completed, final AI response:", lastAiResponseRef.current);

        // ストリーミング完了後、最後のAI応答が存在する場合は確実に表示
        if (lastAiResponseRef.current && lastAiResponseRef.current.trim()) {
          console.log("Ensuring final AI response is displayed");
          
          // AIメッセージが存在しない場合は作成
          if (!aiMessageIdRef.current) {
            aiMessageIdRef.current = (Date.now() + 1).toString();
            const finalAiMessage: Message = {
              id: aiMessageIdRef.current,
              content: lastAiResponseRef.current,
              type: "ai",
              timestamp: new Date(),
              isProcessing: true,
            };
            setMessages((prev) => [...prev, finalAiMessage]);
          } else {
            // 既存のAIメッセージを最終内容で更新
            setMessages((prev) =>
              prev.map((msg) =>
                msg.id === aiMessageIdRef.current
                  ? { ...msg, content: lastAiResponseRef.current }
                  : msg
              )
            );
          }
        }

        // Mark the message as complete（作成済みの場合のみ）
        if (aiMessageIdRef.current) {
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === aiMessageIdRef.current
                ? { ...msg, isProcessing: false }
                : msg
            )
          );
        }
      } catch (error) {
        console.error("Chat error:", error);

        // 作成済みAIメッセージがあれば削除
        if (aiMessageIdRef.current) {
          const removeId = aiMessageIdRef.current;
          setMessages((prev) => prev.filter((msg) => msg.id !== removeId));
        }

        // Don't show error notification if we're resetting, if it's an abort error, or if session changed
        if (!isResetting &&
            !(error instanceof Error && error.name === 'AbortError') &&
            currentSessionIdRef.current === sessionId) {
          addNotification({
            type: "error",
            title: "チャットエラー",
            message:
              error instanceof Error
                ? error.message
                : "メッセージの送信に失敗しました",
          });
        }
      } finally {
        setIsProcessing(false);
        setIsTyping(false);
        setStatusMessages([]);
        firstChunkRef.current = false;
        aiMessageIdRef.current = "";

        lastAiResponseRef.current = "";
        streamCompletedRef.current = false;

        abortControllerRef.current = null;

      }
    },
    [
      isProcessing,
      addNotification,
      messages,
      updateOverview,
      updateSpecificationAnalysis,
      updateReport,
      updateThreadId,
      updateAnalysisHistory,
      isResetting,
    ]
  );

  const getStatusMessage = (currentStep: string): string => {
    switch (currentStep) {
      case "analyze_incident":
        return "AIが問い合わせを分析中です";
      case "collect_incident_info":
        return "AIがインシデントレポート作成処理を開始しています";
      case "analyze_specification":
        return "AIが仕様書の内容を確認しています";
      default:
        return "AIが内容の分析を行なっています。";
    }
  };

  const resetSession = useCallback(
    async (projectId: string) => {
      try {
        console.log("Starting reset session...");
        
        // Cancel any ongoing stream first (before setting resetting flag)
        if (abortControllerRef.current) {
          console.log("Aborting current stream...");
          abortControllerRef.current.abort();
          abortControllerRef.current = null;
        }
        
        // Generate a new session ID to invalidate all old requests
        const newSessionId = Date.now().toString();
        currentSessionIdRef.current = newSessionId;
        console.log("New session ID:", newSessionId);
        
        // Set resetting flag to prevent data updates during reset
        setResetting(true);
        
        // Clear all notifications first to avoid showing old error messages
        clearNotifications();
        
        // Clear data immediately for better UX
        console.log("Clearing all data...");
        setMessages([]);
        setStatusMessages([]);
        currentStepRef.current = "";
        threadIdRef.current = "";
        firstChunkRef.current = false;
        aiMessageIdRef.current = "";
        lastAiResponseRef.current = "";
        streamCompletedRef.current = false;
        clearAll();
        // Clear auto-trigger flag when resetting session
        // This ensures that if user manually resets, the flag is cleared
        // For auto-trigger from URL, ChatPage will set the flag again after resetSession completes
        setIsAutoTriggeringFromUrl(false);
        setHasChatStarted(false);
        
        // Reset processing state
        setIsProcessing(false);
        setIsTyping(false);
        
        // Wait a bit to ensure all pending operations are cancelled
        await new Promise(resolve => setTimeout(resolve, 100));
        
        console.log("Reset session completed successfully");
        
        addNotification({
          type: "success",
          title: "セッションリセット",
          message: "新しいセッションを開始しました",
        });
      } catch (error) {
        console.error("Reset session error:", error);
        addNotification({
          type: "error",
          title: "セッションリセット失敗",
          message:
            error instanceof Error
              ? error.message
              : "セッションリセットに失敗しました",
        });
      } finally {
        // Reset the resetting flag after a delay to ensure all pending updates are blocked
        setTimeout(() => {
          console.log("Resetting flags after delay...");
          setResetting(false);
        }, 500); // Reduced delay to 500ms
      }
    },
    [addNotification, clearAll, setResetting, setIsAutoTriggeringFromUrl, setHasChatStarted]
  );

  const clearMessages = useCallback(() => {
    setMessages([]);
    setStatusMessages([]);
    threadIdRef.current = "";
    currentStepRef.current = "";
    setHasChatStarted(false);
  }, [setHasChatStarted]);

  return {
    messages,
    hasChatStarted: messages.length > 0,
    isProcessing,
    isTyping,
    statusMessages,
    sendMessage,
    resetSession,
    clearMessages,
  };
}

export default useChat;